import type { QueryClient } from '@tanstack/react-query'

import { isPerfEnabled, recordQuery } from './stats'

/**
 * Subscribe to the React Query cache and count fetches per queryKey.
 *
 * Each fetch records: count, last duration, accumulated duration, error tally.
 * Works for both onchain (wagmi `useReadContract(s)`) and indexer/HTTP queries
 * because they all land in the same QueryCache.
 */
export function instrumentQueryCache(queryClient: QueryClient): () => void {
  if (!isPerfEnabled()) return () => {}

  const inFlight = new Map<string, number>()

  return queryClient.getQueryCache().subscribe((event) => {
    const { query } = event
    if (!query) return

    const key = serializeKey(query.queryKey)
    const status = query.state.fetchStatus

    if (event.type === 'updated' && event.action?.type === 'fetch') {
      if (!inFlight.has(key)) {
        inFlight.set(key, now())
      }
      return
    }

    // Finished: either success or failure
    if (event.type === 'updated' && (event.action?.type === 'success' || event.action?.type === 'error')) {
      const startedAt = inFlight.get(key)
      const durationMs = startedAt !== undefined ? now() - startedAt : undefined
      inFlight.delete(key)
      recordQuery(key, durationMs, event.action.type === 'success')
      return
    }

    // Safety net: if somehow we missed a completion event but the query is
    // back to idle, flush any pending timer.
    if (status === 'idle' && inFlight.has(key)) {
      const startedAt = inFlight.get(key)
      const durationMs = startedAt !== undefined ? now() - startedAt : undefined
      inFlight.delete(key)
      recordQuery(key, durationMs, query.state.status !== 'error')
    }
  })
}

function serializeKey(key: unknown): string {
  try {
    return JSON.stringify(key)
  } catch {
    return String(key)
  }
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}
